import { cva } from "class-variance-authority";
import { Check } from "lucide-react";
import type * as React from "react";
import { cn } from "@/lib/utils";

const stepIndicatorIconVariants = cva(
  "relative z-20 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border",
  {
    variants: {
      status: {
        complete: "border-success bg-success text-success-foreground",
        active: "border-foreground bg-foreground text-background",
        incomplete: "border-muted bg-muted text-foreground",
      },
    },
    defaultVariants: {
      status: "incomplete",
    },
  }
);

const stepIndicatorLabelVariants = cva(
  "relative z-20 truncate text-sm leading-[19.6px]",
  {
    variants: {
      status: {
        complete:
          "text-muted-foreground line-through decoration-1 decoration-current",
        active: "font-semibold text-foreground",
        incomplete: "text-muted-foreground/70",
      },
    },
    defaultVariants: {
      status: "incomplete",
    },
  }
);

type DivProps = Omit<React.ComponentProps<"div">, "children">;

export type StepIndicatorProps = {
  status: "incomplete" | "active" | "complete";
  stepNumber: number;
  label: string;
} & DivProps;

function CompleteStep({
  label,
  className,
  ...props
}: { label: string } & DivProps) {
  return (
    <div
      className={cn("flex items-center gap-4", className)}
      data-slot="step-indicator"
      data-status="complete"
      {...props}
    >
      <span className={cn(stepIndicatorIconVariants({ status: "complete" }))}>
        <Check className="h-3.5 w-3.5" />
      </span>
      <span className={cn(stepIndicatorLabelVariants({ status: "complete" }))}>
        {label}
        <span className="sr-only"> — complete</span>
      </span>
    </div>
  );
}

function ActiveStep({
  stepNumber,
  label,
  className,
  ...props
}: { stepNumber: number; label: string } & DivProps) {
  return (
    <div
      className={cn("flex items-center gap-4", className)}
      data-slot="step-indicator"
      data-status="active"
      {...props}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 z-10 h-0.5 bg-foreground"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute top-[-18px] left-1/2 z-0 h-5 w-[70%] -translate-x-1/2 rounded-full bg-primary opacity-100 blur-[18px] saturate-150"
      />
      <span className={cn(stepIndicatorIconVariants({ status: "active" }))}>
        <span className="text-sm">{stepNumber}</span>
      </span>
      <span className={cn(stepIndicatorLabelVariants({ status: "active" }))}>
        {label}
        <span className="sr-only"> — active</span>
      </span>
    </div>
  );
}

function IncompleteStep({
  stepNumber,
  label,
  className,
  ...props
}: { stepNumber: number; label: string } & DivProps) {
  return (
    <div
      className={cn("flex items-center gap-4", className)}
      data-slot="step-indicator"
      data-status="incomplete"
      {...props}
    >
      <span className={cn(stepIndicatorIconVariants({ status: "incomplete" }))}>
        <span className="text-sm">{stepNumber}</span>
      </span>
      <span
        className={cn(stepIndicatorLabelVariants({ status: "incomplete" }))}
      >
        {label}
        <span className="sr-only"> — incomplete</span>
      </span>
    </div>
  );
}

function StepIndicator({
  status,
  stepNumber,
  label,
  className,
  ...props
}: StepIndicatorProps) {
  if (status === "complete") {
    return <CompleteStep className={className} label={label} {...props} />;
  }
  if (status === "active") {
    return (
      <ActiveStep
        className={className}
        label={label}
        stepNumber={stepNumber}
        {...props}
      />
    );
  }
  return (
    <IncompleteStep
      className={className}
      label={label}
      stepNumber={stepNumber}
      {...props}
    />
  );
}

export { StepIndicator, stepIndicatorIconVariants, stepIndicatorLabelVariants };
